import { apiClient } from './api'

export interface StateDefinition {
  key: string
  label: string
  bgColor: string
  textColor: string
  terminal: boolean
  order: number
}

export interface WorkflowTransition {
  from: string
  to: string
  action?: string
}

export interface WorkflowModelVersion {
  workflowVersionId: string
  workflowName: string
  description: string | null
  states: StateDefinition[]
  transitions: WorkflowTransition[]
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
  createdAt: string
  createdBy: string | null
  updatedAt: string
  updatedBy: string | null
  version: number
}

export interface CreateWorkflowRequest {
  workflowName: string
  description?: string
  states?: StateDefinition[]
  transitions?: WorkflowTransition[]
}

export interface UpdateWorkflowRequest {
  workflowName: string
  description?: string
  states: StateDefinition[]
  transitions: WorkflowTransition[]
  version: number
}

export interface WorkflowLintResult {
  pass: boolean
  errors: string[]
  warnings: string[]
}

export class WorkflowService {
  static async getAll(params?: {
    q?: string
    status?: string
    page?: number
    size?: number
  }): Promise<{ items: WorkflowModelVersion[]; total: number }> {
    const queryParams = new URLSearchParams()
    if (params?.q) queryParams.append('q', params.q)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.page) queryParams.append('page', String(params.page))
    if (params?.size) queryParams.append('size', String(params.size))

    const queryString = queryParams.toString()
    const url = queryString ? `/api/workflows?${queryString}` : '/api/workflows'
    return apiClient.get<{ items: WorkflowModelVersion[]; total: number }>(url)
  }

  static async getById(id: string): Promise<WorkflowModelVersion> {
    return apiClient.get<WorkflowModelVersion>(`/api/workflows/${id}`)
  }

  static async create(request: CreateWorkflowRequest): Promise<WorkflowModelVersion> {
    return apiClient.post<WorkflowModelVersion>('/api/workflows', request)
  }

  static async update(id: string, request: UpdateWorkflowRequest): Promise<WorkflowModelVersion> {
    return apiClient.put<WorkflowModelVersion>(`/api/workflows/${id}`, request)
  }

  static async lint(idOrRequest: string | { states: StateDefinition[], transitions: WorkflowTransition[] }): Promise<WorkflowLintResult> {
    if (typeof idOrRequest === 'string') {
      // ID指定版
      return apiClient.post<WorkflowLintResult>(`/api/workflows/${idOrRequest}/lint`, {})
    } else {
      // データ直接指定版
      return apiClient.post<WorkflowLintResult>('/api/workflows/lint', idOrRequest)
    }
  }

  static async publish(id: string): Promise<{ status: string }> {
    return apiClient.post<{ status: string }>(`/api/workflows/${id}/publish`, {})
  }

  static async duplicate(id: string): Promise<WorkflowModelVersion> {
    return apiClient.post<WorkflowModelVersion>(`/api/workflows/${id}/duplicate`, {})
  }
}
